/**
 * SolutionMap UC priority — backend column `priority: Integer ≥ 0`.
 *
 * The prototype cycles between three discrete values that map onto the
 * priority tiers used in `utils/priority.ts`:
 *   10 → strategic (S)
 *    5 → recommended (R)
 *    0 → opportunistic (O)
 *
 * The UC cell shows the single-letter chip (S / R / O); clicking it cycles to
 * the next tier and PUTs `priority: <number>` to UpdateSolutionMap.
 *
 * Distinct from `topicPriorityCycle.ts` (Topic instances), which uses the
 * `notes` column carrying the string 'S' / 'R' / 'O' instead of a number.
 */

export const STRATEGIC = 10;
export const RECOMMENDED = 5;
export const OPPORTUNISTIC = 0;

export type SrOLabel = 'S' | 'R' | 'O';

/** Map a backend priority number to the S/R/O letter, matching the prototype. */
export function priorityToLabel(p: number | null | undefined): SrOLabel {
  const v = p ?? 0;
  if (v >= STRATEGIC) return 'S';
  if (v >= RECOMMENDED) return 'R';
  return 'O';
}

/** Cycle current priority → next priority (10 → 5 → 0 → 10). */
export function nextPriority(p: number | null | undefined): number {
  const v = p ?? 0;
  if (v >= STRATEGIC) return RECOMMENDED;
  if (v >= RECOMMENDED) return OPPORTUNISTIC;
  return STRATEGIC;
}

/** Inverse: convert a letter (e.g. from a form radio) back to the canonical number. */
export function labelToPriority(label: SrOLabel): number {
  switch (label) {
    case 'S':
      return STRATEGIC;
    case 'R':
      return RECOMMENDED;
    case 'O':
    default:
      return OPPORTUNISTIC;
  }
}
